import React from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import { CalendarDayShape } from "react-dates";
import { colors } from "@sonder/ui/source/styled/theme";

const { blue7, gray6, grayE, white } = colors;

interface DayProps {
  isDisabledDay?: boolean;
  isOutsideDay?: boolean;
  isRecurrenceDay?: boolean;
  isSelectedDay?: boolean;
  isToday?: boolean;
}

const TextStyles = css`
  color: ${(props: DayProps) => {
    if (props.isOutsideDay) return "none";
    if (props.isSelectedDay) return white;
    if (props.isToday) return blue7;
    if (props.isDisabledDay) return grayE;
    return gray6;
  }};
  font-size: 1rem;
`;

const BorderStyles = css`
  &:after {
    border: ${(props: DayProps) => {
      if (props.isOutsideDay) return "none";
      if (props.isSelectedDay) return `1px solid ${blue7}`;
      if (props.isToday) return `1px solid ${blue7}`;
      if (props.isDisabledDay) return `1px solid ${grayE}`;
      if (props.isRecurrenceDay) return `1px dashed ${blue7}`;
      return `1px solid ${grayE}`;
    }};
  }
`;

const solidGradient = (color: string, overlayOpacity: number) =>
  `linear-gradient(rgba(0,0,0,${overlayOpacity}), rgba(0,0,0,${overlayOpacity})), linear-gradient(${color}, ${color})`;

const BackgroundStyles = css`
  &:after {
    background: ${(props: DayProps) => {
      if (props.isSelectedDay) return solidGradient(blue7, 0);
      return solidGradient(white, 0);
    }};
  }

  &:hover:after {
    background: ${(props: DayProps) => {
      if (props.isDisabledDay || props.isOutsideDay) return undefined;
      if (props.isSelectedDay) return solidGradient(blue7, 0.05);
      return solidGradient(white, 0.05);
    }};
  }

  &:active::after {
    background: ${(props: DayProps) => {
      if (props.isDisabledDay || props.isOutsideDay) return undefined;
      if (props.isSelectedDay) return solidGradient(blue7, 0.1);
      return solidGradient(white, 0.1);
    }};
  }
`;

export const StyledDay = styled.td<DayProps>`
  position: relative;
  z-index: 0;
  height: 39px;
  width: 39px;
  box-sizing: border-box;
  outline: 0;

  &:after {
    top: 0px;
    left: 0px;
    content: "";
    height: 100%;
    position: absolute;
    width: 100%;
    z-index: -1;
    box-sizing: border-box;
  }
  &:hover {
    cursor: ${(props: DayProps) => {
      if (props.isDisabledDay || props.isOutsideDay) return "initial";
      return "pointer";
    }};
  }

  ${TextStyles}
  ${BorderStyles}
  ${BackgroundStyles}
`;

interface CalendarDayProps extends CalendarDayShape {
  isRecurrenceDay: boolean;
  isDisabledDay: boolean;
}
export const CalendarDay = ({
  day,
  isOutsideDay,
  isRecurrenceDay,
  isDisabledDay,
  modifiers,
  onDayClick,
  onDayMouseEnter,
  onDayMouseLeave,
}: CalendarDayProps) => {
  const isToday = Boolean(day && day.isSame(moment(), "day"));
  const isSelectedDay = Boolean(modifiers && modifiers.has("selected"));

  return (
    <StyledDay
      data-testid={
        !isDisabledDay && !isOutsideDay
          ? "selectable-day"
          : "not-selectable-day"
      }
      isDisabledDay={isDisabledDay}
      isOutsideDay={isOutsideDay}
      isRecurrenceDay={isRecurrenceDay}
      isSelectedDay={isSelectedDay}
      isToday={isToday}
      key={day ? day.date() : Math.random()}
      tabIndex={-1}
      onClick={e => onDayClick(day, e)}
      onMouseEnter={e => onDayMouseEnter(day, e)}
      onMouseLeave={e => onDayMouseLeave(day, e)}
    >
      {day ? day.date() : null}
    </StyledDay>
  );
};
